import type { JSX } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ClientResource,
  type CreateClientBody,
  createClientSchema,
  type QontoClientResource,
} from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import {
  BillingFields,
  billingFieldsSchema,
  type BillingFieldsValues,
  billingPayload,
  billingValuesOf,
  formatAddress,
} from '../components/billing-fields';
import { capLevel, percentUsed, type CapLevel } from '../components/client-helpers';
import { QontoClientSearch } from '../components/QontoClientSearch';
import { PageChrome } from '../components/page-chrome';
import { useToast } from '../components/toast';
import { ApiHttpError, internalFetch, internalSend } from '../lib/api';
import { currentMonth, formatEur, formatEurCents, formatPercent, initials } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { CellUser } from '@/components/ui/avatar';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar } from '@/components/ui/card';
import { MeterCell } from '@/components/ui/chart';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, Fields2, Fieldset, InputGroup, SearchInput } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Row4 } from '@/components/ui/layout-grid';
import { formatMonthLabel, PageHead } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import { clientStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';

/** Identity and caps; the billing details come from Qonto or from `BillingFields` (D-QONTO.8). */
const createFormSchema = createClientSchema.pick({
  name: true,
  safetyCap: true,
  clientCap: true,
  defaultUserCap: true,
});
type FormValues = z.infer<typeof createFormSchema>;

/** Per-client line of `/internal/overview` (same endpoint and cache key as the Overview page). */
interface OverviewLine {
  clientId: string | null;
  billed: string;
  requestCount: number;
}
interface OverviewResponse {
  month: string;
  byClient: OverviewLine[];
  total: OverviewLine;
}

type ClientTab = 'all' | 'active' | 'alert';

interface ClientRow {
  client: ClientResource;
  /** Usage billed this month, from the overview; undefined until it is loaded. */
  billed: string | undefined;
  requestCount: number | undefined;
  /** Percent of the safety cap consumed; null when it cannot be computed. */
  percent: number | null;
  level: CapLevel | null;
}

const LEVEL_BADGES: Record<CapLevel, { variant: BadgeVariant; label: string }> = {
  ok: { variant: 'success', label: 'Dans le seuil' },
  warn: { variant: 'warning', label: 'Seuil à 80 %' },
  over: { variant: 'danger', label: 'Dépassé' },
};

const COLUMN_COUNT = 6;

export function ClientsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<ClientTab>('all');
  const [open, setOpen] = useState(false);
  const month = currentMonth();

  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });
  const overview = useQuery({
    queryKey: ['overview', month],
    queryFn: () => internalFetch<OverviewResponse>(`/internal/overview?month=${month}`),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: { name: '', safetyCap: '' },
  });
  const billingForm = useForm<BillingFieldsValues>({
    resolver: zodResolver(billingFieldsSchema),
    defaultValues: billingValuesOf(null),
  });
  /** The Qonto client picked by the search; null: new details, created in Qonto. */
  const [qontoClient, setQontoClient] = useState<QontoClientResource | null>(null);

  const create = useMutation({
    mutationFn: ({ values, billing }: { values: FormValues; billing: CreateClientBody['billing'] }) => {
      // Strip empty optional cap fields: euroAmountSchema rejects empty strings.
      const payload: Record<string, unknown> = {
        name: values.name,
        billing,
        safetyCap: values.safetyCap,
      };
      if (values.clientCap !== undefined && values.clientCap !== '') {
        payload.clientCap = values.clientCap;
      }
      if (values.defaultUserCap !== undefined && values.defaultUserCap !== '') {
        payload.defaultUserCap = values.defaultUserCap;
      }
      return internalSend<ClientResource>('POST', '/internal/clients', payload);
    },
    onSuccess: (_created, { values }) => {
      resetForms();
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      notify(`Client « ${values.name} » créé`);
    },
  });

  const resetForms = (): void => {
    form.reset();
    billingForm.reset(billingValuesOf(null));
    setQontoClient(null);
  };

  const submit = form.handleSubmit(
    async (values) => {
      if (qontoClient !== null) {
        create.mutate({ values, billing: { source: 'qonto', qontoClientId: qontoClient.id } });
        return;
      }
      await billingForm.handleSubmit((billing) => {
        create.mutate({ values, billing: { source: 'new', ...billingPayload(billing) } });
      })();
    },
    async () => {
      // Show the billing errors too, not only once the identity is valid.
      if (qontoClient === null) {
        await billingForm.trigger();
      }
    },
  );

  const selectQontoClient = (record: QontoClientResource): void => {
    setQontoClient(record);
    if (form.getValues('name') === '' && record.legalName !== null) {
      form.setValue('name', record.legalName);
    }
  };

  const usageByClient = new Map<string, OverviewLine>();
  for (const line of overview.data?.byClient ?? []) {
    if (line.clientId !== null) {
      usageByClient.set(line.clientId, line);
    }
  }
  const overviewLoaded = overview.data !== undefined;
  // Usage figures: « … » while loading, « — » when the overview could not be loaded.
  const usagePlaceholder = overview.isError ? '—' : '…';

  const allRows: ClientRow[] = (clients.data?.clients ?? []).map((client) => {
    const line = usageByClient.get(client.id);
    // A client without activity this month is absent from the overview: zero usage.
    const billed = overviewLoaded ? (line?.billed ?? '0') : undefined;
    const percent = billed === undefined ? null : percentUsed(billed, client.safetyCap);
    return {
      client,
      billed,
      requestCount: overviewLoaded ? (line?.requestCount ?? 0) : undefined,
      percent,
      level: percent === null ? null : capLevel(percent),
    };
  });

  const activeCount = allRows.filter((row) => row.client.status === 'active').length;
  const alertRows = allRows.filter((row) => row.level === 'warn' || row.level === 'over');

  const query = search.trim().toLowerCase();
  const rows = allRows
    .filter((row) => {
      switch (tab) {
        case 'all':
          return true;
        case 'active':
          return row.client.status === 'active';
        case 'alert':
          return row.level === 'warn' || row.level === 'over';
      }
    })
    .filter(
      ({ client }) =>
        query.length === 0 ||
        `${client.name} ${client.billingEmail} ${client.legalName}`.toLowerCase().includes(query),
    );
  const pagination = usePagination(rows, 25);

  const closeModal = (): void => {
    setOpen(false);
    create.reset();
    resetForms();
  };

  const errors = form.formState.errors;
  const firstAlert = alertRows[0];

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Clients' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setOpen(true);
            }}
          >
            <Plus aria-hidden="true" />
            Nouveau client
          </Button>
        }
      />
      <PageHead title="Clients" subtitle="Comptes facturés via la passerelle" />

      <Row4 aria-label="Indicateurs clients">
        <StatCard
          label="Clients actifs"
          value={clients.data === undefined ? '…' : String(activeCount)}
          foot={
            clients.data === undefined
              ? undefined
              : `sur ${allRows.length} compte${allRows.length > 1 ? 's' : ''}`
          }
        />
        <StatCard
          label="Usage du mois"
          value={
            overview.data === undefined
              ? usagePlaceholder
              : formatEurCents(overview.data.total.billed)
          }
          foot={`${formatMonthLabel(month)}, HT`}
        />
        <StatCard
          label="Seuil à 80 % atteint"
          value={
            overviewLoaded && clients.data !== undefined
              ? String(alertRows.length)
              : usagePlaceholder
          }
          foot={
            firstAlert !== undefined &&
            firstAlert.billed !== undefined &&
            firstAlert.percent !== null
              ? `${firstAlert.client.name}, ${formatPercent(firstAlert.percent)} de ${formatEurCents(firstAlert.client.safetyCap)}`
              : 'du plafond de sécurité'
          }
        />
      </Row4>

      <Card>
        <CardBar
          start={
            <TableTabs<ClientTab>
              aria-label="Filtrer les clients"
              value={tab}
              onValueChange={setTab}
              items={[
                {
                  value: 'all',
                  label: 'Tous',
                  count: clients.data === undefined ? undefined : allRows.length,
                },
                {
                  value: 'active',
                  label: 'Actifs',
                  count: clients.data === undefined ? undefined : activeCount,
                },
                {
                  value: 'alert',
                  label: 'Seuil atteint',
                  count:
                    overviewLoaded && clients.data !== undefined ? alertRows.length : undefined,
                },
              ]}
            />
          }
          end={
            <SearchInput
              label="Rechercher un client"
              placeholder="Rechercher un client ou un email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          }
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead kind="name">Client</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Usage du mois (HT)</TableHead>
              <TableHead>Seuil</TableHead>
              <TableHead kind="num">Plafond de sécurité</TableHead>
              <TableHead kind="num">Requêtes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagination.pageItems.map((row) => {
              const { client } = row;
              const level = row.level === null ? null : LEVEL_BADGES[row.level];
              return (
                <TableRow
                  key={client.id}
                  onClick={() =>
                    void navigate({ to: '/clients/$clientId', params: { clientId: client.id } })
                  }
                >
                  <TableCell kind="name">
                    <CellUser initials={initials(client.name)}>
                      <Cell2
                        primary={
                          <Link
                            to="/clients/$clientId"
                            params={{ clientId: client.id }}
                            className={rowLinkClass}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {client.name}
                          </Link>
                        }
                        secondary={client.billingEmail}
                      />
                    </CellUser>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={clientStatusLabel(client.status)} />
                  </TableCell>
                  <TableCell>
                    {row.billed === undefined ? (
                      <span className="text-muted-foreground">{usagePlaceholder}</span>
                    ) : (
                      <MeterCell
                        percent={row.percent ?? 0}
                        start={formatEurCents(row.billed)}
                        end={row.percent === null ? '' : formatPercent(row.percent)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {level === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Badge variant={level.variant}>{level.label}</Badge>
                    )}
                  </TableCell>
                  <TableCell kind="num">{formatEur(client.safetyCap)}</TableCell>
                  <TableCell kind="num">
                    {row.requestCount === undefined ? usagePlaceholder : String(row.requestCount)}
                  </TableCell>
                </TableRow>
              );
            })}
            {clients.isLoading ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
            ) : null}
            {rows.length === 0 && !clients.isLoading ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>Aucun client ne correspond.</TableEmptyRow>
            ) : null}
          </TableBody>
        </Table>
        <Pager itemLabel="clients" {...pagination.pagerProps} />
      </Card>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeModal();
          }
        }}
      >
        <DialogContent>
          <DialogHead title="Nouveau client" description="Créer un compte facturable." />
          <form
            id="create-client-form"
            onSubmit={(event) => {
              void submit(event);
            }}
            className="contents"
          >
            <DialogBody>
              <Fieldset
                title="Facturation"
                description="Qonto détient la fiche de facturation : choisir un client Qonto, ou en créer un."
              >
                {qontoClient !== null ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium">
                        {qontoClient.legalName}
                        <span className="ml-2 font-mono text-muted-foreground">
                          {qontoClient.siren}
                        </span>
                      </div>
                      <div className="text-caption text-muted-foreground">
                        {qontoClient.email} · {formatAddress(qontoClient.billingAddress)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setQontoClient(null);
                      }}
                    >
                      Changer
                    </Button>
                  </div>
                ) : (
                  <>
                    <QontoClientSearch onSelect={selectQontoClient} />
                    <p className="m-0 text-caption text-muted-foreground">
                      Client absent de Qonto ? Renseigner sa fiche : elle sera créée dans Qonto.
                    </p>
                    <BillingFields form={billingForm} />
                  </>
                )}
              </Fieldset>

              <Fieldset title="Identité">
                <Field
                  label="Nom"
                  required
                  hint="Nom affiché dans la console."
                  error={errors.name?.message}
                >
                  <Input placeholder="Acme" {...form.register('name')} />
                </Field>
              </Fieldset>

              <Fieldset title="Plafonds">
                <Field
                  label="Plafond de sécurité"
                  required
                  hint="Met le client en pause au dépassement."
                  error={errors.safetyCap?.message}
                >
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      placeholder="1000"
                      {...form.register('safetyCap')}
                    />
                  </InputGroup>
                </Field>
                <Fields2>
                  <Field label="Plafond client" error={errors.clientCap?.message}>
                    <InputGroup suffix="€">
                      <Input
                        numeric
                        inputMode="decimal"
                        placeholder="Optionnel"
                        {...form.register('clientCap')}
                      />
                    </InputGroup>
                  </Field>
                  <Field
                    label="Plafond utilisateur par défaut"
                    error={errors.defaultUserCap?.message}
                  >
                    <InputGroup suffix="€">
                      <Input
                        numeric
                        inputMode="decimal"
                        placeholder="Optionnel"
                        {...form.register('defaultUserCap')}
                      />
                    </InputGroup>
                  </Field>
                </Fields2>
              </Fieldset>

              {create.isError ? (
                <Callout variant="destructive">
                  {create.error instanceof ApiHttpError && create.error.detail !== null
                    ? create.error.detail
                    : 'Échec de la création.'}
                </Callout>
              ) : null}
            </DialogBody>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit" form="create-client-form" size="lg" disabled={create.isPending}>
              Créer le client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
